<!-- Profile Page -->
<div class="profile-page">
  <!-- Page Header -->
  <div class="page-header mb-4">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="page-title">
          <i class="bi bi-person-circle me-2"></i>
          My Profile
        </h1>
        <p class="page-subtitle text-muted mb-0">
          View and manage your personal information
        </p>
      </div>
      <div>
        <button
          *ngIf="!isEditMode"
          type="button"
          class="btn btn-primary"
          (click)="toggleEditMode()"
          [disabled]="isLoading"
        >
          <i class="bi bi-pencil me-2"></i>
          Edit Profile
        </button>
        <button
          *ngIf="isEditMode"
          type="button"
          class="btn btn-outline-secondary me-2"
          (click)="toggleEditMode()"
          [disabled]="isSaving"
        >
          Cancel
        </button>
        <button
          *ngIf="isEditMode"
          type="button"
          class="btn btn-success"
          (click)="saveProfile()"
          [disabled]="isSaving || profileForm.invalid"
        >
          <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
          Save Changes
        </button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-muted mt-3">Loading profile...</p>
  </div>

  <!-- Profile Content -->
  <div *ngIf="!isLoading" class="row">
    <!-- Profile Card -->
    <div class="col-lg-4 mb-4">
      <div class="card">
        <div class="card-body text-center">
          <div class="profile-avatar mb-3">
            <i class="bi bi-person-circle fs-5rem"></i>
          </div>
          <h4 class="mb-1">{{ profileForm.get('firstName')?.value }} {{ profileForm.get('lastName')?.value }}</h4>
          <p class="text-muted mb-3">{{ userEmail }}</p>
        </div>
      </div>
    </div>

    <!-- Profile Form -->
    <div class="col-lg-8">
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Personal Information</h5>
        </div>
        <div class="card-body">
          <form [formGroup]="profileForm">
            <div class="row g-3">
              <!-- First Name -->
              <div class="col-md-6">
                <label for="firstName" class="form-label">First Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="firstName"
                  formControlName="firstName"
                  [readonly]="!isEditMode"
                  [class.is-invalid]="isEditMode && profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched"
                >
                <div class="invalid-feedback" *ngIf="profileForm.get('firstName')?.errors?.['required']">
                  First name is required
                </div>
              </div>

              <!-- Last Name -->
              <div class="col-md-6">
                <label for="lastName" class="form-label">Last Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="lastName"
                  formControlName="lastName"
                  [readonly]="!isEditMode"
                  [class.is-invalid]="isEditMode && profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched"
                >
                <div class="invalid-feedback" *ngIf="profileForm.get('lastName')?.errors?.['required']">
                  Last name is required
                </div>
              </div>

              <!-- Email (Read-only) -->
              <div class="col-md-6">
                <label for="email" class="form-label">Email</label>
                <input
                  type="email"
                  class="form-control"
                  id="email"
                  [value]="userEmail"
                  readonly
                >
                <small class="text-muted">Email cannot be changed</small>

              </div>
              
              <!-- Phone -->
              <div class="col-md-6">
                <app-form-input
                  label="Phone"
                  type="tel"
                  mask="phone"
                  formControlName="phone"
                  placeholder="(555) 123-4567">
                </app-form-input>
                @if(!phoneVerified && userPhone) {
                  <small class="text-muted">
                    <button class="btn btn-link btn-sm p-0 align-baseline" type="button" (click)="verifyPhone()">Verify</button>
                    to enable phone login and password reset
                  </small>
                }
              </div>
              
              <!-- Date of Birth -->
              <div class="col-md-6">
                <label for="dateOfBirth" class="form-label">Date of Birth</label>
                <input
                  type="date"
                  class="form-control"
                  id="dateOfBirth"
                  formControlName="dateOfBirth"
                  [readonly]="!isEditMode"
                >
              </div>

              <!-- Spacer -->
              <div class="col-md-6"></div>

              <!-- Address Section Header -->
              <div class="col-12">
                <h6 class="mt-3 mb-0">Address</h6>
                <hr class="mt-2">
              </div>

              <!-- Address -->
              <div class="col-12">
                <label for="address" class="form-label">Street Address</label>
                <input
                  type="text"
                  class="form-control"
                  id="address"
                  formControlName="address"
                  [readonly]="!isEditMode"
                  placeholder="123 Main St"
                >
              </div>

              <!-- City -->
              <div class="col-md-5">
                <label for="city" class="form-label">City</label>
                <input
                  type="text"
                  class="form-control"
                  id="city"
                  formControlName="city"
                  [readonly]="!isEditMode"
                >
              </div>

              <!-- State -->
              <div class="col-md-3">
                <label for="state" class="form-label">State</label>
                @if(!isEditMode) {
                  <input
                    type="text"
                    class="form-control"
                    [readOnly]="true"
                    formControlName="state"
                  >
                } @else {
                  <app-us-state-select inputId="state" formControlName="state" display="both"></app-us-state-select>
                }
              </div>

              <!-- Zip Code -->
              <div class="col-md-4">
                <label for="zipCode" class="form-label">Zip Code</label>
                <input
                  type="text"
                  class="form-control"
                  id="zipCode"
                  formControlName="zipCode"
                  [readonly]="!isEditMode"
                  maxlength="10"
                >
              </div>

              <!-- Coordinates Section Header -->
              <div class="col-12 mt-3 d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Coordinates</h6>
                @if (!isEditMode) {
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    (click)="lookupCoordinates()">
                    <i class="bi bi-crosshair me-1"></i>
                    Look up
                  </button>
                } 
              </div>
              @if (lookupError && !isEditMode) {
                  <div class="col-12">
                    <div class="alert alert-warning d-flex align-items-start mb-0" role="alert">
                      <i class="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                      <div>
                        <div class="fw-semibold">{{ lookupError }}</div>
                        <div class="small">
                          You can enter the coordinates yourself.
                            <button type="button" class="btn btn-link btn-sm alert-link p-0 align-baseline" (click)="toggleEditMode()">
                              Edit profile
                            </button>
                            to enter them.
                        </div>
                      </div>
                    </div>
                  </div>
                }
              <div class="col-md-6">
                <label for="latitude" class="form-label">Latitude</label>
                <input
                  type="number"
                  class="form-control"
                  id="latitude"
                  formControlName="latitude"
                  [readonly]="!isEditMode"
                  [class.is-invalid]="profileForm.get('latitude')?.invalid && profileForm.get('latitude')?.touched"
                >
                @if (profileForm.get('latitude')?.invalid) {
                  <div class="invalid-feedback">Latitude must be between -90 and 90</div>
                }
              </div>

              <!-- Longitude -->
              <div class="col-md-6">
                <label for="longitude" class="form-label">Longitude</label>
                <input
                  type="number"
                  class="form-control"
                  id="longitude"
                  formControlName="longitude"
                  [readonly]="!isEditMode"
                  [class.is-invalid]="profileForm.get('longitude')?.invalid && profileForm.get('longitude')?.touched"
                >
                @if (profileForm.get('longitude')?.invalid) {
                  <div class="invalid-feedback">Longitude must be between -180 and 180</div>
                }
              </div>
            </div>
          </form>
        </div>
      </div>

      <!--
        Danger zone — destructive account actions. Visually segregated
        from the editable profile form so it can't be triggered by
        accident. Routes into the full delete-account flow which gates
        on deletion preconditions + requires password re-auth.
      -->
      <div class="card mt-4 danger-zone-card">
        <div class="card-header bg-danger-subtle">
          <h5 class="card-title mb-0 text-danger bg-danger-subtle">
            <i class="bi bi-exclamation-triangle me-2"></i>Danger zone
          </h5>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <strong>Delete account</strong>
              <p class="text-muted small mb-0">
                Permanently remove your login. Compliance records are retained as
                required by law — review the full disclosure on the next screen
                before confirming.
              </p>
            </div>
            <a routerLink="/profile/delete-account" class="btn btn-outline-danger">
              <i class="bi bi-trash me-2"></i>Delete Account
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- coordinates lookup confirmation modal that shows the found coordinates plus a map  -->
@if (pendingCoordinates) {
  <div class="modal-backdrop fade show" (click)="declinedCoordinates()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-dialog-centered modal-lg w-100" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-geo-alt me-2"></i>Confirm Coordinates</h5>
          <button type="button" class="btn-close" (click)="declinedCoordinates()" [disabled]="savingCoordinates" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <p class="text-body-secondary small mb-3">
            Found from: {{ profileForm.value.address }}, {{ profileForm.value.city }}, {{ profileForm.value.state }} {{ profileForm.value.zipCode }}
          </p>

          <div class="row g-3 mb-3">
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Current</label>
              @if (profileForm.value.latitude != null) {
                <div>{{ profileForm.value.latitude | number:'1.5-5' }}, {{ profileForm.value.longitude | number:'1.5-5' }}</div>
              } @else {
                <div class="text-body-secondary fst-italic">Not set</div>
              }
            </div>
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">New</label>
              <div class="fw-semibold">
                {{ pendingCoordinates.lat | number:'1.5-5' }}, {{ pendingCoordinates.lng | number:'1.5-5' }}
              </div>
            </div>
          </div>

          <div class="rounded-2 border overflow-hidden">
            <google-map
              [center]="pendingCoordinates"
              [zoom]="15"
              [options]="mapOptions"
              width="100%"
              height="320px">
              <map-marker [position]="pendingCoordinates"></map-marker>
            </google-map>
          </div>

          @if (saveError) {
            <div class="alert alert-danger d-flex align-items-start mt-3 mb-0" role="alert">
              <i class="bi bi-x-circle-fill me-2 mt-1"></i>
              <div>
                <div class="fw-semibold">Save failed</div>
                <div class="small">{{ saveError }}</div>
              </div>
            </div>
          }
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="declinedCoordinates()" [disabled]="savingCoordinates">
            Decline
          </button>
          <button type="button" class="btn btn-primary" (click)="confirmedCoordinates()" [disabled]="savingCoordinates">
            @if (savingCoordinates) {
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            }
            {{ savingCoordinates ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
}
